import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from "@angular/core";
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HttpClient,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import Aura from "@primeng/themes/aura";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { routes } from "./app.routes";
import { provideServiceWorker } from "@angular/service-worker";
import { GetInterceptor } from "./core/interceptors/get-interceptor.service";
import { SetInterceptor } from "./core/interceptors/set-interceptor.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { NgxUiLoaderHttpModule, NgxUiLoaderModule } from "ngx-ui-loader";
import { AppStateService } from "./services/app-state.service";
import { firstValueFrom } from "rxjs";
import { ProfileService } from "./services/profile.service";
import { UserService } from "./services/user.service";
import { environment } from "../environments/environment";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(async () => {
      const appState = inject(AppStateService);
      const profileService = inject(ProfileService);
      const userService = inject(UserService);

      const token = localStorage.getItem(environment.storageKey);

      const calls: Promise<any>[] = [
        // Always fetch app config (theme, logo, name) — needed even on login page
        firstValueFrom(profileService.getAppSetting())
          .then((res: any) => {
            appState.setAppConfig({
              platform_name: res?.platform_name,
              theme_color: res?.theme_color,
              company_logo: res?.company_logo?.s3_file_url ?? null,
            });
          })
          .catch(() => {}),
      ];

      // Only fetch user profile if logged in
      if (token) {
        calls.push(
          firstValueFrom(userService.getProfile())
            .then((res: any) => {
              appState.setUser(res?.data ?? null);
            })
            .catch(() => {}),
        );
      }

      await Promise.all(calls);
    }),
    provideHttpClient(withInterceptorsFromDi()),

    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: "en",
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      NgxUiLoaderModule,
      NgxUiLoaderHttpModule.forRoot({
        showForeground: true,
        excludeRegexp: [
          "auth/setings/",
          "auth/user-edit/",
          "global-search/",
          "insight-admin/",
        ],
      }),
    ),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: "enabled",
        scrollPositionRestoration: "enabled",
      }),
      withEnabledBlockingInitialNavigation(),
    ),
    provideAnimationsAsync(),
    provideServiceWorker("ngsw-worker.js", {
      enabled: true,
      registrationStrategy: "registerWhenStable:3000",
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GetInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SetInterceptor,
      multi: true,
    },
    MessageService,

    providePrimeNG({
      theme: {
        preset: Aura,
        // eslint-disable-next-line no-constant-binary-expression
        options: { mode: "light", darkModeSelector: false || "none" },
      },
    }),
    ConfirmationService,
  ],
};
